import axios from "axios";

// Cache to store option data responses
const optionDataCache = new Map();

// Cache expiration time (5 minutes)
const CACHE_EXPIRY_TIME = 5 * 60 * 1000;

/**
 * Generate a consistent cache key for symbol and date
 * @param {string} symbol - The symbol to fetch data for
 * @param {string} date - The expiry date
 * @returns {string} The cache key
 */
const generateCacheKey = (symbol, date) => {
    // Normalize the inputs to ensure consistent cache keys
    const normalizedSymbol = symbol?.toUpperCase()?.trim();
    const normalizedDate = date?.trim();
    return `${normalizedSymbol}_${normalizedDate}`;
};

/**
 * Get cached option data or fetch from API if not cached
 * @param {string} symbol - The symbol to fetch data for
 * @param {string} date - The expiry date
 * @returns {Promise<Object>} The option data
 */
export const getCachedOptionData = async (symbol, date) => {
    const cacheKey = generateCacheKey(symbol, date);
    const now = Date.now();
    
    // Check if data exists in cache and is not expired
    if (optionDataCache.has(cacheKey)) {
        const cachedData = optionDataCache.get(cacheKey);
        if (now - cachedData.timestamp < CACHE_EXPIRY_TIME) {
            return cachedData.data;
        } else {
            // Remove expired cache entry
            // console.log(`⏰ Removing expired cache entry for ${cacheKey}`);
            optionDataCache.delete(cacheKey);
        }
    }
    
    // If not in cache or expired, fetch from API
    // console.log(`🔄 Fetching option data for ${cacheKey}`);
    const data = await fetchOptionDataFromAPI(symbol, date);
    
    if (data) {
        // Store in cache with timestamp
        optionDataCache.set(cacheKey, {
            data,
            timestamp: now
        });
    }
    
    return data;
};

/**
 * Fetch option data from API (original implementation)
 * @param {string} symbol - The symbol to fetch data for
 * @param {string} date - The expiry date
 * @returns {Promise<Object>} The option data
 */
const fetchOptionDataFromAPI = async (symbol, date) => {
    let brokerName = localStorage.getItem("broker");

    if (!brokerName) {
        console.error("Broker name not found in local storage.");
        return null;
    }

    brokerName = brokerName.toLowerCase().replace(/\s+/g, "");
    const apiEndpoint = `https://y18wfq6w5b.execute-api.ap-south-1.amazonaws.com/default/og_expirysymbols`;
    
    try {
        const response = await axios.post(apiEndpoint, {
            id: symbol + date,
            broker: brokerName,
        });
        const data = response.data;
        return data;
    } catch (error) {
        console.error("Error fetching option data:", error);
        return null;
    }
};

/**
 * Clear the cache (useful for testing or manual cache management)
 */
export const clearOptionDataCache = () => {
    const size = optionDataCache.size;
    optionDataCache.clear();
    // console.log(`🗑️ Option data cache cleared (${size} entries removed)`);
};

/**
 * Get cache statistics (useful for debugging)
 */
export const getCacheStats = () => {
    const now = Date.now();
    const entries = Array.from(optionDataCache.entries()).map(([key, value]) => ({
        key,
        age: now - value.timestamp,
        isExpired: (now - value.timestamp) >= CACHE_EXPIRY_TIME
    }));
    
    return {
        size: optionDataCache.size,
        entries,
        cacheExpiryTime: CACHE_EXPIRY_TIME
    };
};

/**
 * Remove expired entries from cache
 */
export const cleanupExpiredCache = () => {
    const now = Date.now();
    let removedCount = 0;
    
    for (const [key, value] of optionDataCache.entries()) {
        if (now - value.timestamp >= CACHE_EXPIRY_TIME) {
            optionDataCache.delete(key);
            removedCount++;
        }
    }
    
    if (removedCount > 0) {
        // console.log(`🧹 Cleaned up ${removedCount} expired cache entries`);
    }
    
    return removedCount;
};

/**
 * Preload option data for a symbol and date combination
 * @param {string} symbol - The symbol to preload
 * @param {string} date - The expiry date
 * @returns {Promise<Object>} The preloaded data
 */
export const preloadOptionData = async (symbol, date) => {
    // console.log(`🚀 Preloading option data for ${symbol}_${date}`);
    return await getCachedOptionData(symbol, date);
};

// Export the original function name for backward compatibility
export const getOptionData = getCachedOptionData;
